
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";
import { AnalysisErrorType } from "./types";
import * as Sentry from "@sentry/react";
import { startContentFetchTransaction, configureContentFetchScope } from "./utils/sentryHelpers";
import { handleFetchError } from "./utils/errorHandlers";

/**
 * Fetch website content using Supabase Edge Function
 * @param url The website URL to fetch
 * @returns The website content as text
 */
export async function fetchWebsiteContent(url: string): Promise<string> {
  const span = startContentFetchTransaction();

  try {
    configureContentFetchScope(url);

    logger.info({
      message: "Fetching website content",
      context: { url }
    });

    const { data: fetchData, error: fetchError } = await supabase.functions.invoke('analyze-website', {
      body: { url, fetchContentOnly: true },
      headers: { prefer: 'return=minimal' }
    });
    
    if (fetchError) {
      logger.error({
        message: "Supabase function error when fetching content",
        error: fetchError,
        context: { url }
      });
      throw new Error(fetchError.message || "Failed to fetch website content");
    }
    
    if (!fetchData || !fetchData.content) {
      logger.error({
        message: "No content returned from website",
        context: { url, response: JSON.stringify(fetchData) }
      });
      throw new Error("No content returned from website");
    }

    const contentLength = fetchData.content.length;
    
    logSuccessfulFetch(url, contentLength);

    span.finish();
    return fetchData.content;
  } catch (error) {
    handleFetchError(error, url);
    span.finish();
    throw error;
  }
}

/**
 * Log successful content fetch
 * @param url The website URL
 * @param contentLength The length of fetched content
 */
function logSuccessfulFetch(url: string, contentLength: number): void {
  logger.info({
    message: "Successfully fetched website content",
    context: { 
      url, 
      contentLength 
    }
  });
}
